<template>
  <div
    class="cxm-ProgressBar"
    :style="cssVars"
  >
    <div class="cxm-ProgressBar-Labels">
      <div class="cxm-ProgressBar-ExportLabel">
        {{ title }}
      </div>
      <div
        v-show="icons"
        class="cxm-ProgressBar-Icons"
      >
        <!-- Baixar -->
        <div v-show="iconDownload">
          <Tooltip
            :text="downloadTooltip"
            position="top left"
            width="300px"
          >
            <Icon
              v-show="isComplete"
              name="Download"
              size="20"
              color="var(--track-color-bluegray-70)"
              :handle-click="downloadButton"
              data-test-id="progress-bar-action-download"
            />
          </Tooltip>
        </div>

        <!-- Remover -->
        <div>
          <Tooltip
            :text="deleteTooltip"
            position="top left"
            width="300px"
          >
            <Icon
              v-show="isComplete"
              name="Trash"
              size="20"
              color="var(--track-color-bluegray-70)"
              :handle-click="deleteButton"
              data-test-id="progress-bar-action-remove"
            />
          </Tooltip>
        </div>

        <!-- Cancelar -->
        <div v-show="iconCancel && !isComplete">
          <Tooltip
            :text="cancelTooltip"
            position="top left"
            width="300px"
          >
            <Icon
              name="Remove"
              size="20"
              color="var(--track-color-bluegray-70)"
              :handle-click="cancelButton"
              data-test-id="progress-bar-action-cancel"
            />
          </Tooltip>
        </div>
      </div>
    </div>
    <div
      class="cxm-ProgressBar-ProgressContainer"
    >
      <Progress
        class="cxm-ProgressBar-Component"
        :color="color"
        :indeterminate="indeterminate"
        :count="value"
        :steps="steps"
      />
    </div>
    <div
      class="cxm-ProgressBar-ProgressInfo"
    >
      <span class="cxm-ProgressBar-ExportInfo">{{ info }}</span>
      <div class="cxm-ProgressBar-ExportMessage">
        {{ message }}
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '../Icon/Icon.vue';
import Progress from '../Progress/Progress.vue';
import Tooltip from '../Tooltip/Tooltip.vue';

export default {
  name: 'ProgressBar',
  components: {
    Icon,
    Progress,
    Tooltip,
  },
  props: {
    color: {
      type: String,
      default: 'brand-primary',
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    info: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    isComplete: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    cancel: {
      type: Function,
      default: () => null,
    },
    download: {
      type: Function,
      default: () => null,
    },
    cancelTooltip: {
      type: String,
      default: '',
    },
    downloadTooltip: {
      type: String,
      default: '',
    },
    hasIcon: {
      type: Boolean,
      default: true,
    },
    iconCancel: {
      type: Boolean,
      default: true,
    },
    iconDownload: {
      type: Boolean,
      default: true,
    },
    width: {
      type: String,
      default: '100%',
    },
    steps: {
      type: Number,
      default: 60,
      required: false,
    },
    deleteTooltip: {
      type: String,
      default: '',

    },
  },
  data() {
    return {
      value: 0,
      icons: true,
    };
  },
  computed: {
    cssVars() {
      return {
        '--width': this.width,
      };
    },
  },
  watch: {
    hasError() {
      this.verifyStatus();
    },

    count(value) {
      this.value = value;
    },
  },
  mounted() {
    this.icons = this.hasIcon;
    this.value = this.count;
    this.verifyStatus();
  },
  methods: {
    cancelButton() {
      this.cancel();
      this.value = 0;
      // eslint-disable-next-line vue/no-mutating-props
      this.indeterminate = false;
    },
    downloadButton() {
      this.download();
    },
    deleteButton() {
      this.$emit('delete');
    },
    verifyStatus() {
      if (this.hasError) {
        this.value = 0;
      } else if (this.indeterminate) {
        this.value = 30;
        this.icons = true;
      } else if (this.isComplete) {
        this.value = this.steps;
        this.icons = true;
      } else {
        this.icons = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cxm-ProgressBar {
  display: flex;
  flex-direction: column;
  width: var(--width);

}

.cxm-ProgressBar-ProgressInfo {
  display: flex;
  justify-content: space-between;
  margin-top: var(--track-spacing-sm);
}

.cxm-ProgressBar-ProgressContainer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  .cxm-ProgressBar-Component {
    width: 100%;
  }
}
.cxm-ProgressBar-Icons {
  margin-left: var(--track-spacing-sm);
  display: flex;
  gap: var(--track-spacing-sm);
}
.cxm-ProgressBar-Labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--track-spacing-sm);
  height: var(--track-font-size-md);
}
.cxm-ProgressBar-ExportLabel {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--track-font-family-base);
  font-size: var(--track-font-size-sm);
  color: var(--track-color-bluegray-70);
  font-weight: var(--track-font-weight-medium);
}
.cxm-ProgressBar-ExportMessage {
  font-family: var(--track-font-family-base);
  font-size: var(--track-font-size-xs);
  color: var(--track-color-bluegray-70);
  font-weight: var(--track-font-weight-medium);
}
.cxm-ProgressBar-ExportInfo {
  font-family: var(--track-font-family-base);
  font-size: var(--track-font-size-xs);
  color: var(--track-color-bluegray-60);
  font-weight: var(--track-font-weight-medium);
}
</style>
